import { useTranslation } from 'react-i18next'
import type { EstadoCamion } from '@/types'
import type { DockVista } from '@/utils/dockTimeTracks'
import { COLOR_ESTADO } from '@/utils/camionesMuelle'
import DateNavPicker from '@/components/common/DateNavPicker'

export interface FiltroCuadro {
  /** Código de camión, matrícula, transportista o nº de carga. */
  busqueda: string
  /** Vacío = todos. */
  estado: EstadoCamion | ''
  transportista: string
}

export const FILTRO_CUADRO_VACIO: FiltroCuadro = { busqueda: '', estado: '', transportista: '' }

export function contarFiltrosCuadro(f: FiltroCuadro): number {
  return [f.busqueda, f.estado, f.transportista].filter(Boolean).length
}

const ESTADOS: EstadoCamion[] = ['programado', 'cargando', 'enRiesgo', 'salido', 'anulado']

/**
 * Los controles del cuadro: qué almacén, qué ventana y qué se busca dentro.
 *
 * El almacén siempre tiene un valor elegido: un muelle pertenece a uno, y
 * mezclar los de VID con los de GAVA daría filas que no comparten ni horario ni
 * realidad.
 *
 * El filtro de estado son píldoras con su color, no un desplegable: son cinco y
 * el color es el mismo que llevan las barras, así que filtrar y leer el cuadro
 * usan el mismo vocabulario.
 */
export function MuellesFiltros({
  almacen, almacenes, onAlmacen,
  vista, onVista,
  offset, onOffset,
  etiquetaRango,
  filtro, onFiltro,
  transportistas,
  dark,
  onGestionarSlots,
}: {
  almacen: string
  almacenes: string[]
  onAlmacen: (v: string) => void
  vista: DockVista
  onVista: (v: DockVista) => void
  offset: number
  onOffset: (n: number) => void
  etiquetaRango: string
  filtro: FiltroCuadro
  onFiltro: (f: FiltroCuadro) => void
  transportistas: string[]
  dark: boolean
  onGestionarSlots: () => void
}) {
  const { t } = useTranslation()
  const activos = contarFiltrosCuadro(filtro)

  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const campo = `px-2 py-1.5 text-[12px] rounded-lg border ${
    dark ? 'bg-slate-800 border-white/10 text-slate-100 placeholder:text-slate-500'
         : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400'
  }`
  const seg = (activo: boolean) =>
    `px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
      activo ? 'bg-white text-slate-900 shadow-sm' : (dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700')
    }`

  return (
    <div className={`rounded-xl border ${bdr} ${dark ? 'bg-slate-900/60' : 'bg-white'}`}>
      {/* Primera fila: dónde y cuándo. */}
      <div className={`flex flex-wrap items-center gap-2 p-2.5 border-b ${bdr}`}>
        <div className={`flex gap-0.5 p-0.5 rounded-lg ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}>
          {almacenes.map((a) => (
            <button key={a} type="button" className={seg(a === almacen)} onClick={() => onAlmacen(a)}>
              {a}
            </button>
          ))}
        </div>

        <div className={`flex gap-0.5 p-0.5 rounded-lg ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}>
          <button type="button" className={seg(vista === 'semana')} onClick={() => onVista('semana')}>
            {t('muelles.vista.semana')}
          </button>
          <button type="button" className={seg(vista === 'dia')} onClick={() => onVista('dia')}>
            {t('muelles.vista.dia')}
          </button>
        </div>

        {/* El mismo selector que el Gantt de producción: las flechas para moverse
            de uno en uno y el calendario para saltar a una fecha concreta. */}
        <DateNavPicker
          mode={vista === 'dia' ? 'day' : 'week'}
          value={offset}
          onChange={onOffset}
          label={etiquetaRango}
          dark={dark}
          btnClass={`px-2.5 py-1 text-[12px] font-semibold rounded-md transition-colors ${
            dark ? 'text-slate-200 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'
          }`}
        />

        <button
          type="button"
          onClick={onGestionarSlots}
          className={`ml-auto px-2.5 py-1 text-[11px] font-semibold rounded-md ${
            dark ? 'text-slate-300 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          {t('muelles.slots.gestionar')}
        </button>
      </div>

      {/* Segunda fila: qué se busca dentro. */}
      <div className="flex flex-wrap items-center gap-2 p-2.5">
        <input
          className={`${campo} w-56`}
          value={filtro.busqueda}
          onChange={(e) => onFiltro({ ...filtro, busqueda: e.target.value })}
          placeholder={t('muelles.filtros.buscar')}
        />

        <div className="flex flex-wrap items-center gap-1">
          {ESTADOS.map((e) => {
            const activo = filtro.estado === e
            return (
              <button
                key={e}
                type="button"
                onClick={() => onFiltro({ ...filtro, estado: activo ? '' : e })}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold
                  ring-1 ring-inset transition-colors
                  ${activo
                    ? (dark ? 'bg-white/10 ring-white/25 text-slate-100' : 'bg-slate-900 ring-slate-900 text-white')
                    : (dark ? 'ring-white/10 text-slate-400 hover:text-slate-200' : 'ring-slate-200 text-slate-600 hover:bg-slate-50')}`}
              >
                <span className={`w-2 h-2 rounded-sm ${COLOR_ESTADO[e].barra}`} aria-hidden />
                {t(`muelles.estado.${e}`)}
              </button>
            )
          })}
        </div>

        {transportistas.length > 0 && (
          <select
            className={campo}
            value={filtro.transportista}
            onChange={(e) => onFiltro({ ...filtro, transportista: e.target.value })}
          >
            <option value="">{t('muelles.filtros.todosTransportistas')}</option>
            {transportistas.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        )}

        {activos > 0 && (
          <button
            type="button"
            onClick={() => onFiltro(FILTRO_CUADRO_VACIO)}
            className="ml-auto text-[11px] font-semibold text-sky-500 hover:underline"
          >
            {t('muelles.filtros.limpiar', { count: activos })}
          </button>
        )}
      </div>
    </div>
  )
}
